import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Warning, CaretRight, MagnifyingGlass, ArrowClockwise } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABEL = {
  candidate: "Candidato",
  employee: "Dipendente",
  management: "Manager",
  team: "Team",
  unknown: "Sconosciuto",
};

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("it-IT"); } catch { return s; }
};

/**
 * B82 P0.1(follow-up) — Elenco report calcolati con motore pre-HMAC
 * (scoring_version != "b72.3"). Read-only: nessuna rigenerazione
 * automatica, l'admin decide caso per caso.
 */
export default function AdminReportsAffectedPage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expandedWs, setExpandedWs] = useState(null);
  const [detailsCache, setDetailsCache] = useState({});
  const [detailsLoading, setDetailsLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reports-affected`, { withCredentials: true });
      setSummary(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const loadDetails = async (wsId) => {
    if (detailsCache[wsId]) return;
    setDetailsLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reports-affected/${wsId}`, { withCredentials: true });
      setDetailsCache((prev) => ({ ...prev, [wsId]: data.reports || [] }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore dettagli");
    } finally {
      setDetailsLoading(false);
    }
  };

  const toggleExpand = (wsId) => {
    if (expandedWs === wsId) { setExpandedWs(null); return; }
    setExpandedWs(wsId);
    loadDetails(wsId);
  };

  const filtered = (summary?.workspaces || []).filter((w) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (
      (w.workspace_name || "").toLowerCase().includes(s) ||
      (w.company_name || "").toLowerCase().includes(s) ||
      (w.owner_email || "").toLowerCase().includes(s) ||
      (w.workspace_id || "").toLowerCase().includes(s)
    );
  });

  return (
    <div className="animate-fade-in p-6 max-w-[1400px] mx-auto" data-testid="admin-reports-affected-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0D1F3C] flex items-center gap-2">
            <Warning size={24} className="text-amber-600" weight="fill" />
            Report con calcolo pre-HMAC
          </h1>
          <p className="text-sm text-[#0D1F3C]/60 mt-1">
            Report generati prima del fix del 21/07/2026, con punteggi non affidabili.
            La rigenerazione si decide caso per caso — nessuna azione automatica di massa.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="reload-btn">
          <ArrowClockwise size={16} className="mr-1" /> Aggiorna
        </Button>
      </div>

      {/* Riepilogo cumulativo */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="admin-reports-affected-summary">
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="text-xs text-amber-700 mb-1">Totale report affetti</div>
              <div className="text-3xl font-bold text-amber-900" data-testid="grand-total">
                {summary.grand_total}
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#0D1F3C]/10">
            <CardContent className="p-4">
              <div className="text-xs text-[#0D1F3C]/50 mb-1">Workspace coinvolti</div>
              <div className="text-3xl font-bold text-[#0D1F3C]" data-testid="workspaces-count">
                {summary.workspaces_count}
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#0D1F3C]/10 col-span-2">
            <CardContent className="p-4">
              <div className="text-xs text-[#0D1F3C]/50 mb-1">Distribuzione per tipo</div>
              <div className="flex flex-wrap gap-2 mt-1">
                {Object.entries(summary.grand_by_type || {}).map(([t, n]) => (
                  <Badge key={t} variant="outline" className="text-sm">
                    {TYPE_LABEL[t] || t}: <span className="font-semibold ml-1">{n}</span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4 max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D1F3C]/40" />
        <Input
          placeholder="Cerca workspace, azienda, email admin..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9"
          data-testid="search-input"
        />
      </div>

      {loading ? (
        <div className="text-sm text-[#0D1F3C]/50 py-12 text-center" data-testid="loading">Caricamento...</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-[#0D1F3C]/50 py-12 text-center" data-testid="empty-state">
          {q.trim() ? "Nessun workspace corrisponde alla ricerca." : "Nessun report pre-HMAC trovato. Tutto ok."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="workspaces-list">
          {filtered.map((w) => {
            const isExpanded = expandedWs === w.workspace_id;
            return (
              <Card key={w.workspace_id} className="border-[#0D1F3C]/8 hover:border-amber-300 transition-all"
                data-testid={`workspace-row-${w.workspace_id}`}>
                <CardContent className="p-0">
                  <button
                    type="button"
                    onClick={() => toggleExpand(w.workspace_id)}
                    className="w-full text-left p-4 flex items-center gap-3 hover:bg-amber-50/30"
                    data-testid={`expand-btn-${w.workspace_id}`}
                  >
                    <CaretRight size={16} className={`text-[#0D1F3C]/40 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#0D1F3C]">
                        {w.workspace_name}
                        {w.company_name && <span className="text-[#0D1F3C]/50 font-normal ml-2">· {w.company_name}</span>}
                      </div>
                      <div className="text-xs text-[#0D1F3C]/50 mt-0.5 truncate">
                        {w.owner_email || "(admin sconosciuto)"} · <code className="text-[10px]">{w.workspace_id}</code>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <div className="text-right">
                        <div className="text-2xl font-bold text-amber-700" data-testid={`total-${w.workspace_id}`}>
                          {w.total_affected}
                        </div>
                        <div className="text-[10px] text-[#0D1F3C]/40 uppercase">report</div>
                      </div>
                      <div className="hidden sm:flex flex-col text-xs text-[#0D1F3C]/60 min-w-[220px]">
                        <div>Test compilato: <span className="font-medium">{fmtDate(w.oldest_test_at)}</span>{w.newest_test_at && w.oldest_test_at !== w.newest_test_at && <> → {fmtDate(w.newest_test_at)}</>}</div>
                        <div>Report generato: <span className="font-medium">{fmtDate(w.oldest_at)}</span>{w.newest_at && w.oldest_at !== w.newest_at && <> → {fmtDate(w.newest_at)}</>}</div>
                      </div>
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="border-t border-[#0D1F3C]/8 bg-[#F8FAFC] p-4">
                      <div className="flex flex-wrap gap-1 mb-3">
                        {Object.entries(w.by_type || {}).map(([t, n]) => (
                          <Badge key={t} variant="secondary" className="text-xs">
                            {TYPE_LABEL[t] || t}: {n}
                          </Badge>
                        ))}
                      </div>
                      {detailsLoading && !detailsCache[w.workspace_id] ? (
                        <div className="text-xs text-[#0D1F3C]/50">Caricamento dettagli...</div>
                      ) : (
                        <div className="space-y-1 max-h-96 overflow-y-auto" data-testid={`details-${w.workspace_id}`}>
                          {(detailsCache[w.workspace_id] || []).map((r) => (
                            <div key={r.report_id} className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-white text-xs">
                              <Badge variant="outline" className="text-[10px] shrink-0">
                                {TYPE_LABEL[r.type] || r.type}
                              </Badge>
                              <div className="flex-1 min-w-0 truncate text-[#0D1F3C]">{r.name}</div>
                              <span className="text-[10px] text-red-600 bg-red-50 px-1.5 py-0.5 rounded shrink-0">
                                {r.test_scoring_version || r.scoring_version || "missing"}
                              </span>
                              <div className="flex flex-col items-end shrink-0 min-w-[130px]">
                                <span className="text-[10px] text-[#0D1F3C]/50">
                                  test: <span className="font-medium">{fmtDate(r.test_completed_at)}</span>
                                </span>
                                <span className="text-[10px] text-[#0D1F3C]/40">
                                  report: {fmtDate(r.report_created_at || r.created_at)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
